// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceDetailCopyReport.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var CategoryDetail = require('logic/models/Izenda.CategoryDetail');
  const { isEmpty, some, forEach, map, isNil, flow, getOr, get, has, compact, find } = require('lodash/fp');

  function markCopiedItem(item, flag) {
    item.checked = flag;
  }

  function markCopied(item) {
    markCopiedItem(item, true);
  }

  function unmarkCopied(item) {
    markCopiedItem(item, false);
  }

  module.exports = function(BaseClass) {
    /**
     * Set source reports to copy
     * @param  {object} options
     */
    BaseClass.prototype.setSourceReports = m.action(function(reportList, templateList) {
      // reset current data
      this.resetCopyReportSource();

      var workspace = this;

      // set report list to model
      setData.call(this, reportList, 'sourceReports');
      setData.call(this, templateList, 'sourceTemplates');

      // reaction - [checked] value is changed, search linked query source and update [belongToCopiedReport] value
      this.addActionToReportList(get('categories', this.sourceReports), addActionForCheckedStatus);
      this.addActionToReportList(get('categories', this.sourceTemplates), addActionForCheckedStatus);

      m.observe(
        this,
        'copyReport',
        function() {
          var sourceReports = get('categories', this.sourceReports);
          var sourceTemplates = get('categories', this.sourceTemplates);

          if (workspace.copyReport) {
            this.addActionToReportList(sourceReports, markCopied, true);
            this.addActionToReportList(sourceTemplates, markCopied, true);
          } else {
            this.addActionToReportList(sourceReports, unmarkCopied, true);
            this.addActionToReportList(sourceTemplates, unmarkCopied, true);
          }

          // update checked of root element
          this.sourceReports.checked = !some({ checked: false }, sourceReports);
          this.sourceTemplates.checked = !some({ checked: false }, sourceTemplates);
        }.bind(this)
      );

      function setData(listData, sourceKey) {
        var categories = map(function(item) {
          return new CategoryDetail(item, true);
        }, listData);
        var checked = !some({ checked: false }, categories);

        this[sourceKey] = !isEmpty(listData)
          ? {
              name: 'All',
              id: 'All',
              checked: checked,
              indeterminate: false,
              categories: categories
            }
          : [];
      }

      function addActionForCheckedStatus(report) {
        report.checked && linkToSourceConnection(report);

        // reaction for checked prop
        m.observe(report, 'checked', function(change) {
          linkToSourceConnection(report);
        });
      }

      function linkToSourceConnection(report) {
        var linkedQuerySouces;
        var isChecked = report.checked ? 1 : -1;

        // search linked query source in sourceConnection
        linkedQuerySouces = workspace.findQuerySouceFromConnectionSource(report.reportDataSource);

        linkedQuerySouces.forEach(function(querySource) {
          workspace.updateBelongToCopiedReport(isChecked, querySource);
        });
      }
    });

    BaseClass.prototype.updateBelongToCopiedReport = m.action(function(isChecked, querySouce) {
      if (isNil(querySouce)) {
        return null;
      }

      if (!has('numberLinkedCopiedReports', querySouce)) {
        querySouce.numberLinkedCopiedReports = 0;
      }

      querySouce.numberLinkedCopiedReports += isChecked;
      querySouce.numberLinkedCopiedReports < 0 && (querySouce.numberLinkedCopiedReports = 0);
      querySouce.belongToCopiedReport = querySouce.numberLinkedCopiedReports > 0;
    });

    BaseClass.prototype.findQuerySouceFromConnectionSource = function(querySouceInfos) {
      var sourceConnections = this.sourceConnections;
      var result = map(findQuerySouce, querySouceInfos || []);

      return compact(result);

      function findQuerySouce(querySourceInfo) {
        return flow(
          find({ id: querySourceInfo.connectionId }), // find based on connectionID

          getOr([], 'dbSource.querySources'), // find based on categories
          find({ id: querySourceInfo.querySourceCategoryId }),

          getOr([], 'querySources'), // find based on query source object
          find({ id: querySourceInfo.querySourceId })
        )(sourceConnections);
      }
    };

    BaseClass.prototype.addActionToReportList = function(reportCat, fnAction, isApplyToCat) {
      this.addActionByCategories('reports', reportCat, fnAction, isApplyToCat);
    };

    BaseClass.prototype.addActionByCategories = function(dataKey, categories, fnAction, isApplyToCat) {
      forEach(function(category) {
        // report of category
        takeAction(category);
        isApplyToCat && fnAction(category);

        // report of sub-category
        forEach(function(subCat) {
          takeAction(subCat);
          isApplyToCat && fnAction(subCat);
        }, category.subCategories);
      }, categories);

      function takeAction(category) {
        forEach(fnAction, getOr([], dataKey, category));
      }
    };

    BaseClass.prototype.resetCopyReportSource = function() {
      // unlink current reports to data-model query sources
      var sourceReports = get('categories', this.sourceReports);
      var sourceTemplates = get('categories', this.sourceTemplates);
      sourceReports && this.addActionToReportList(sourceReports, unmarkCopied, true);
      sourceTemplates && this.addActionToReportList(sourceTemplates, unmarkCopied, true);

      this.sourceReports = {};
      this.sourceTemplates = {};
    };

    /**
     * Set source dashboards
     * @param {Object} dashboard list
     * @param {Boolean} For Concurrency
     */
    BaseClass.prototype.setSourceDashboards = m.action(function setSourceDashboards(dashboardList, forConcurrency) {
      // reset current data
      ((this.copyDashboard && !this.copyReport) || forConcurrency) && this.resetCopyDashboardSource();

      var workspace = this;
      var categories = map(function(item) {
        return new CategoryDetail(item, true);
      }, dashboardList);
      var checked = !some({ checked: false }, categories);

      // asign dashboard source
      this.sourceDashboards = !isEmpty(dashboardList)
        ? {
            name: 'All',
            id: 'All',
            checked: checked,
            indeterminate: false,
            categories: categories
          }
        : [];

      // reaction - [checked] value is changed, search linked query source and update [belongToCopiedReport] value
      this.addActionToDashboardList(get('categories', this.sourceDashboards), addActionForCheckedStatus);

      m.observe(
        this,
        'copyDashboard',
        function() {
          var copyDashboard = this.copyDashboard;

          this.addActionToDashboardList(
            get('categories', this.sourceDashboards),
            function(item) {
              item.checked = copyDashboard;
            },
            true
          );

          // update checked of root element
          this.sourceDashboards.checked = !some({ checked: false }, this.sourceDashboards);
        }.bind(this)
      );

      function addActionForCheckedStatus(dashboard) {
        dashboard.checked && linkToSourceConnection(dashboard);

        // reaction for checked prop
        m.observe(dashboard, 'checked', function(change) {
          linkToSourceConnection(dashboard);
        });

        function linkToSourceConnection(dashboard) {
          var isChecked = dashboard.checked ? 1 : -1;
          var linkedQuerySouces;

          dashboard.dashboardParts.forEach(function(dashboardPart) {
            linkedQuerySouces = workspace.findQuerySouceFromConnectionSource(dashboardPart.reportDataSource || []);
            linkedQuerySouces.forEach(function(querySource) {
              workspace.updateBelongToCopiedReport(isChecked, querySource);
            });
          });
        }
      }
    });

    BaseClass.prototype.addActionToDashboardList = function(dashboardCat, fnAction, isApplyToCat) {
      this.addActionByCategories('dashboards', dashboardCat, fnAction, isApplyToCat);
    };

    BaseClass.prototype.resetCopyDashboardSource = function() {
      // unlink current reports to data-model query sources
      var sourceDashboards = get('categories', this.sourceDashboards);
      sourceDashboards && this.addActionToDashboardList(sourceDashboards, unmarkCopied, true);

      this.sourceDashboards = {};
    };
  };
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.WorkspaceDetailCopyReport.js